import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  Person,
  Phone,
  BusinessCenter,
  CalendarToday,
  Schedule,
  AccountCircle,
  Home,
  Celebration,
  DarkMode,
  LightMode,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useThemeMode } from "../contexts/ThemeContext";
import ModernHeader from "../components/ModernHeader";

// Animated Background
const AnimatedBackground = ({ mode }) => {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 0,
        overflow: "hidden",
        background: mode === "dark" 
          ? "linear-gradient(135deg, #FFFEF9 0%, #F5F0E8 50%, #FFFEF9 100%)"
          : "linear-gradient(135deg, #F5F0E8 0%, #FAF7F2 50%, #F5F0E8 100%)",
      }}
    >
      {/* Animated Gradient Orbs */}
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: mode === "dark" ? [0.3, 0.5, 0.3] : [0.15, 0.25, 0.15],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        style={{
          position: "absolute",
          top: "10%",
          right: "15%",
          width: "400px",
          height: "400px",
          background: mode === "dark"
            ? "radial-gradient(circle, rgba(16, 185, 129, 0.4) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
        }}
      />
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: mode === "dark" ? [0.2, 0.4, 0.2] : [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
        style={{
          position: "absolute",
          bottom: "15%",
          left: "15%",
          width: "500px",
          height: "500px",
          background: mode === "dark"
            ? "radial-gradient(circle, rgba(149, 213, 178, 0.3) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(149, 213, 178, 0.12) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(80px)",
        }}
      />

      {/* Success Particles */}
      {[...Array(25)].map((_, i) => (
        <motion.div
          key={i}
          animate={{
            y: [0, -120, 0],
            x: [0, Math.random() * 60 - 30, 0],
            opacity: mode === "dark" ? [0, 1, 0] : [0, 0.4, 0],
            rotate: [0, 360],
          }}
          transition={{
            duration: Math.random() * 6 + 4,
            repeat: Infinity,
            delay: Math.random() * 3,
          }}
          style={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            width: "4px",
            height: "4px",
            background: mode === "dark" ? "#52B788" : "#2D6A4F",
            borderRadius: "50%",
            boxShadow: mode === "dark" ? "0 0 15px #52B788" : "0 0 8px rgba(45, 106, 79, 0.5)",
          }}
        />
      ))}
    </Box>
  );
};

// Confetti Component
const Confetti = () => {
  const colors = ["#52B788", "#95D5B2", "#2D6A4F", "#74C69D", "#d4edda"];
  
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {[...Array(50)].map((_, i) => (
        <motion.div
          key={i}
          initial={{
            x: `${Math.random() * 100}vw`,
            y: -20,
            rotate: 0,
          }}
          animate={{
            y: "110vh",
            rotate: 360,
            x: `${Math.random() * 100}vw`,
          }}
          transition={{
            duration: Math.random() * 3 + 2,
            delay: Math.random() * 0.5,
            ease: "easeIn",
          }}
          style={{
            position: "absolute",
            width: Math.random() * 10 + 5,
            height: Math.random() * 10 + 5,
            background: colors[Math.floor(Math.random() * colors.length)],
            opacity: 0.8,
          }}
        />
      ))}
    </Box>
  );
};

// Motion Components
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

export default function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state || {};
  const [showConfetti, setShowConfetti] = useState(true);
  const { mode, toggleMode } = useThemeMode();

  const handleBookingClick = useCallback(() => navigate("/booking"), [navigate]);
  const goToServices = useCallback(() => navigate("/#services"), [navigate]);
  const goToContact = useCallback(() => navigate("/#contact"), [navigate]);

  useEffect(() => {
    // Hide confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    return () => clearTimeout(timer);
  }, []);

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const checkmarkVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        duration: 0.8,
      },
    },
  };

  const pulseVariants = {
    animate: {
      scale: [1, 1.1, 1],
      opacity: [0.5, 0.8, 0.5],
    },
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  const bookingDetails = [
    {
      icon: Person,
      label: "Name of Applicant",
      value: booking.name || "-",
      colors: { dark: "#52B788", light: "#2D6A4F" },
    },
    {
      icon: Phone,
      label: "Contact Details",
      value: booking.contact || "-",
      colors: { dark: "#95D5B2", light: "#52B788" },
    },
    {
      icon: BusinessCenter,
      label: "Service",
      value: booking.service || "-",
      colors: { dark: "#2D6A4F", light: "#1B4D35" },
    },
    {
      icon: CalendarToday,
      label: "Date",
      value: booking.date || "-",
      colors: { dark: "#fbbf24", light: "#f59e0b" },
    },
    {
      icon: Schedule,
      label: "Time",
      value: booking.time_slot || "-",
      colors: { dark: "#f87171", light: "#ef4444" },
    },
    {
      icon: AccountCircle,
      label: "Assigned Employee",
      value: booking.employee || "-",
      colors: { dark: "#818cf8", light: "#6366f1" },
    },
  ];

  return (
    <>
      <AnimatedBackground mode={mode} />
      
      <ModernHeader
        mode={mode}
        toggleMode={toggleMode}
        onBookingClick={handleBookingClick}
        onServicesClick={goToServices}
        onContactClick={goToContact}
      />

      {/* Confetti Animation */}
      <AnimatePresence>
        {showConfetti && <Confetti />}
      </AnimatePresence>

      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 3, sm: 4, md: 5 },
          px: { xs: 2, sm: 3 },
          position: "relative", 
          zIndex: 1,
          backgroundColor: "#ffffff",
        }}
      >
        {/* Theme Toggle Button */}
        <Box sx={{ position: "fixed", top: { xs: 12, sm: 16 }, right: { xs: 12, sm: 16 }, zIndex: 1300 }}>
          <Tooltip title={mode === "dark" ? "Switch to light mode" : "Switch to dark mode"} arrow>
            <IconButton
              onClick={toggleMode}
              sx={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                border: mode === "dark" ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
                color: mode === "dark" ? "#fbbf24" : "#818cf8",
                "&:hover": {
                  background: "rgba(255, 255, 255, 1)",
                  transform: "scale(1.1)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {mode === "dark" ? <LightMode /> : <DarkMode />}
            </IconButton>
          </Tooltip>
        </Box>
        <MotionPaper
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          elevation={0}
          sx={{
            borderRadius: { xs: 4, sm: 6 },
            p: { xs: 2.5, sm: 4, md: 5 },
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            position: "relative",
            overflow: "hidden",
            mx: { xs: 0, sm: 0 },
          }}
        >
          {/* Success Glow Effect */}
          <Box
            sx={{
              position: "absolute",
              top: -100,
              left: "50%",
              transform: "translateX(-50%)",
              width: "300px",
              height: "300px",
              background: mode === "dark"
                ? "radial-gradient(circle, rgba(16, 185, 129, 0.3) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 70%)",
              filter: "blur(60px)",
              zIndex: 0,
            }}
          />

          {/* Success Icon */}
          <MotionBox
            initial="hidden"
            animate="visible"
            variants={checkmarkVariants}
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Pulsing Ring */}
              <motion.div
                animate={pulseVariants.animate}
                transition={pulseVariants.transition}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "120px",
                  height: "120px",
                  borderRadius: "50%",
                  border: `3px solid ${mode === "dark" ? "#52B788" : "#2D6A4F"}`,
                  zIndex: 0,
                }}
              />
              
              {/* Success Icon */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: mode === "dark"
                    ? "linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.4) 100%)"
                    : "linear-gradient(135deg, rgba(5, 150, 105, 0.15) 0%, rgba(5, 150, 105, 0.25) 100%)",
                  border: mode === "dark" ? "3px solid #52B788" : "3px solid #2D6A4F",
                  boxShadow: mode === "dark" 
                    ? "0 0 40px rgba(16, 185, 129, 0.6)" 
                    : "0 0 30px rgba(5, 150, 105, 0.3)",
                }}
              >
                <CheckCircle sx={{ fontSize: 60, color: mode === "dark" ? "#52B788" : "#2D6A4F" }} />
              </Box>
            </Box>
          </MotionBox>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.75rem", sm: "2.125rem", md: "2.5rem" },
                background: mode === "dark"
                  ? "linear-gradient(135deg, #2D6A4F 0%, #52B788 50%, #95D5B2 100%)"
                  : "linear-gradient(135deg, #2D6A4F 0%, #52B788 50%, #95D5B2 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                position: "relative",
                zIndex: 1,
                px: { xs: 1, sm: 0 },
                mb: { xs: 1, sm: 2 },
              }}
            >
              Booking Confirmed!
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Celebration sx={{ color: mode === "dark" ? "#fbbf24" : "#f59e0b", fontSize: 24 }} />
              <Celebration sx={{ color: mode === "dark" ? "#52B788" : "#2D6A4F", fontSize: 24 }} />
              <Celebration sx={{ color: mode === "dark" ? "#95D5B2" : "#52B788", fontSize: 24 }} />
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Typography
              variant="body1"
              align="center"
              sx={{
                color: mode === "dark" ? "#94a3b8" : "#64748b",
                mb: 4,
                fontSize: "1.1rem",
                position: "relative",
                zIndex: 1,
              }}
            >
              Thank you for booking your service slot. We've received your request and
              will get back to you shortly.
            </Typography>
          </motion.div>

          <Divider
            sx={{
              mb: 4,
              background: mode === "dark"
                ? "linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.5), transparent)"
                : "linear-gradient(90deg, transparent, rgba(5, 150, 105, 0.4), transparent)",
              height: "2px",
              border: "none",
            }}
          />

          {/* Booking Details */}
          <MotionBox
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            sx={{
              my: 4,
              position: "relative",
              zIndex: 1,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: mode === "dark" ? "#ffffff" : "#000000",
                  fontWeight: 700,
                  mb: { xs: 2, sm: 3 },
                  fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  textAlign: "center",
                }}
              >
                Your Booking Details
              </Typography>
            </motion.div>

            <List sx={{ p: 0 }}>
              {bookingDetails.map((detail, index) => {
                const accent = mode === "dark" ? detail.colors.dark : detail.colors.light;
                return (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, x: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ListItem
                      sx={{
                        mb: { xs: 1.5, sm: 2 },
                        p: { xs: 2, sm: 2.5, md: 3 },
                        borderRadius: { xs: 2, sm: 3 },
                        background: `linear-gradient(135deg, ${accent}10 0%, ${accent}05 100%)`,
                        border: `1px solid ${accent}40`,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${accent}20 0%, ${accent}10 100%)`,
                          borderColor: accent,
                          boxShadow: `0 4px 20px ${accent}30`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            p: { xs: 1.25, sm: 1.5 },
                            borderRadius: 2,
                            background: `${accent}20`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: { xs: 48, sm: 56 },
                            minHeight: { xs: 48, sm: 56 },
                          }}
                        >
                          <detail.icon sx={{ fontSize: { xs: 24, sm: 28 }, color: accent }} />
                        </Box>
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#000000",
                                fontWeight: 600,
                                mb: 0.5,
                                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                              }}
                            >
                              {detail.label}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body1"
                              sx={{
                                color: "#000000",
                                fontWeight: 700,
                                fontSize: { xs: "0.95rem", sm: "1.1rem" },
                                wordBreak: "break-word",
                              }}
                            >
                              {detail.value}
                            </Typography>
                          }
                        />
                      </Box>
                    </ListItem>
                  </motion.div>
                );
              })}
            </List>
          </MotionBox>

          {/* Action Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <motion.div
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                component={Link}
                to="/"
                variant="contained"
                fullWidth
                size="large"
                startIcon={<Home />}
                sx={{
                  mt: { xs: 3, sm: 4 },
                  py: { xs: 1.75, sm: 2 },
                  borderRadius: { xs: 2, sm: 3 },
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 700,
                  minHeight: { xs: "48px", sm: "56px" },
                  background: "linear-gradient(135deg, #52B788 0%, #95D5B2 100%)",
                  color: "white",
                  border: "2px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 8px 30px rgba(82, 183, 136, 0.4)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s",
                  },
                  "&:hover": {
                    background: "linear-gradient(135deg, #95D5B2 0%, #52B788 100%)",
                    boxShadow: "0 12px 40px rgba(82, 183, 136, 0.6)",
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                Back to Home
              </Button>
            </motion.div>
          </motion.div>

          {/* Info Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
          >
            <Typography
              variant="caption"
              align="center"
              sx={{
                display: "block",
                mt: 3,
                color: mode === "dark" ? "#94a3b8" : "#64748b",
                fontStyle: "italic",
              }}
            >
              A confirmation email has been sent to your registered contact details
            </Typography>
          </motion.div>

        </MotionPaper>

        {/* Additional Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.8 }}
        >
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: 3,
              background: "rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 0, 0, 0.1)",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" sx={{ color: mode === "dark" ? "#94a3b8" : "#4a5568", mb: 2 }}>
              Need to make changes to your booking?
            </Typography>
            <Chip
              label="Contact Support"
              sx={{
                background: mode === "dark"
                  ? "linear-gradient(135deg, rgba(82, 183, 136, 0.2) 0%, rgba(149, 213, 178, 0.2) 100%)"
                  : "linear-gradient(135deg, rgba(82, 183, 136, 0.15) 0%, rgba(149, 213, 178, 0.15) 100%)",
                border: mode === "dark"
                  ? "1px solid rgba(82, 183, 136, 0.3)"
                  : "1px solid rgba(82, 183, 136, 0.2)",
                color: mode === "dark" ? "#52B788" : "#2D6A4F",
                fontWeight: 600,
                "&:hover": {
                  background: mode === "dark"
                    ? "linear-gradient(135deg, rgba(82, 183, 136, 0.3) 0%, rgba(149, 213, 178, 0.3) 100%)"
                    : "linear-gradient(135deg, rgba(82, 183, 136, 0.25) 0%, rgba(149, 213, 178, 0.25) 100%)",
                },
              }}
            />
          </Box>
        </motion.div>
      </Container>
    </>
  );
}
